/**
 * VISTA AUTOMATICA FRONTE/RETRO (fork, tutti gli schermi) — richiesta di Davide del 26 set 2026:
 *  - quando il cliente SCRIVE la dedica, il gioiello si gira da solo sul retro (dove va l'incisione);
 *  - quando cambia modello, forma, segnaposto, finitura o misura, il gioiello torna DRITTO;
 *  - (6 ott) FORMA e FORMA ANELLO viaggiano insieme quando si cambia Modello (vedi sotto).
 * Lo fa impostando il parametro "Mostra il retro" (0 = Fronte, 1 = Retro) come farebbe il cliente, ma SENZA un
 * calcolo in piu' (6 ott: valore "committato" che viaggia nel calcolo della scelta, vedi committaValore).
 * Sull'anello il modello ignora comunque il retro (l'incisione sta dentro il gambo).
 * Sul configuratore standard (senza fork) questa logica non c'e': vale solo qui.
 */
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {useShapeDiverStoreSession} from "@AppBuilderLib/entities/session/model/useShapeDiverStoreSession";
import {useShapeDiverStoreViewport} from "@AppBuilderLib/entities/viewport/model/useShapeDiverStoreViewport";
import {IShapeDiverParameter} from "@AppBuilderLib/entities/parameter/config/parameter";
import {useEffect} from "react";
import {sceltaGioiello, zoomDedicaOff, zoomDedicaOn, zoomDedicaTesto} from "./zoomDedica";
import {chiaveFinitura, finituraCambiata, ricoloraMetallo, smaltoCambiato, useColoriNitidi} from "./finituraIstantanea";

/** fattore di zoom "normale" (tutto il gioiello): sul totem 1,55, altrove 1,5 (come in EidosTotem) */
function fattoreNormale(): number {
	return document.body.classList.contains("eidos-totem") ? 1.55 : 1.5;
}

const NOME_INCISIONE = "Incisione";
const NOME_RETRO = "Mostra il retro";
const NOME_DEDICA = "Dedica";
/** cambiando uno di questi il gioiello torna al fronte */
const RIMETTONO_DRITTO = ["Modello", "Forma", "Forma anello", "Segnaposto", "Finitura", "Misura", "Taglia anello", "Smalto", "Cornice"];

/** (6 ott 2026 pomeriggio, Davide) FORMA e FORMA ANELLO viaggiano insieme: scegliendo "Anello" l'anello si apre con la
 *  forma gia' scelta nel menu FORMA (prima usciva sempre il quadrifoglio), e tornando a collana/bracciale/orecchini
 *  la forma e' quella che aveva l'anello. Il valore viene "committato" (setCommittedValue dello store ShapeDiver)
 *  nell'istante in cui cambia il Modello, PRIMA che parta il suo calcolo: cosi' viaggia nello stesso calcolo e non
 *  ne costa uno in piu'. "Goccia" e "— scegli —" non esistono per l'anello: in quei casi l'anello resta com'era. */
const NOME_MODELLO = "Modello";
const NOME_FORMA = "Forma";
const NOME_FORMA_ANELLO = "Forma anello";
const NOME_ANELLO = "anello";
/** (7 ott sera) la finitura cambia il colore SUBITO nel visore, il calcolo arriva dopo (vedi finituraIstantanea.ts) */
const NOME_FINITURA = "Finitura";
const NOME_SMALTO = "Smalto";
// (12 ott sera) Gioiello Sagoma: al cambio di forma della maschera la foto torna a zoom 100 (= incisione massima dentro la forma,
// la calcola il modello) e spostamento 0, nello stesso calcolo del cambio
const NOME_ZOOM_SAGOMA = "Zoom sagoma";
const NOME_SPOSTA_X = "Sposta X";
const NOME_SPOSTA_Y = "Sposta Y";
// (14 ott sera) anche la CORNICE cambia lo spazio della foto: la foto si rimassimizza nello stesso calcolo
const CAMBIANO_MASCHERA = ["Modello", "Forma", "Misura", "Forma anello", "Cornice"];
const NOME_FOTO = "Foto";

/** nome (minuscolo) della voce scelta in un menu; v = indice da leggere, altrimenti il valore appena scelto (uiValue) */
function nomeScelta(store: Store | undefined, v?: string): string {
	if (!store) return "";
	const p = store.getState() as IShapeDiverParameter<string>;
	const scelte = (p.definition as {choices?: string[]}).choices ?? [];
	const testo = String(v ?? p.state.uiValue ?? "");
	const i = parseInt(testo, 10);
	return (isNaN(i) ? testo : (scelte[i] ?? "")).trim().toLowerCase();
}
function indiceScelta(store: Store | undefined, nome: string): number {
	if (!store || !nome) return -1;
	const p = store.getState() as IShapeDiverParameter<string>;
	const scelte = (p.definition as {choices?: string[]}).choices ?? [];
	return scelte.findIndex((sc) => sc.trim().toLowerCase() === nome);
}
/** scrive la voce i nel menu SENZA far partire un calcolo: parte con il prossimo (quello del cambio modello) */
function committa(store: Store | undefined, i: number) {
	if (!store || i < 0) return;
	committaValore(store, String(i));
}

type Store = ReturnType<ReturnType<typeof useShapeDiverStoreParameters.getState>["getParameter"]>;

/** il valore APPENA SCELTO (uiValue) di un parametro, come testo */
function ui(store: Store | undefined): string {
	if (!store) return "";
	return String((store.getState() as IShapeDiverParameter<string>).state.uiValue ?? "");
}

/** (6 ott pomeriggio) scrive un valore SENZA far partire un calcolo suo: viaggia nel calcolo che sta per partire.
 *  Fino al 6 ott "Mostra il retro" veniva impostato con setUiValue + execute DOPO il calcolo della scelta (si
 *  guardava l'execValue): con una dedica scritta ogni scelta costava DUE calcoli (+1-1,3 s e crediti doppi).
 *  Ora si ascolta lo uiValue: il callback gira dentro setUiValue, PRIMA dell'execute della scelta, e il valore
 *  committato parte nello stesso customize (stesso trucco di Forma <-> Forma anello qui sotto). */
function committaValore(store: Store | undefined, v: string) {
	if (!store) return;
	const p = store.getState() as IShapeDiverParameter<string>;
	if (String(p.state.uiValue ?? "") === v) return;
	const azioni = p.actions as unknown as {setCommittedValue?: (x: string) => boolean};
	if (azioni.setCommittedValue) azioni.setCommittedValue(v);
	else if (p.actions.setUiValue(v)) p.actions.execute(true);
}

/**
 * LUCE DEL GIOIELLO SAGOMA (Davide, 13 ott 2026: «illumina la scena in modo che la vista di partenza sia illuminata come
 * quando giro il gioiello»). La piastrina del Týpos e' PIENA e liscia: vista di fronte fa da specchio e rifletteva la
 * zona scura della mappa d'ambiente "jewelry_studio" (oro marrone). Si ruota la mappa d'ambiente in modo che il suo
 * softbox principale (a ~130° di lato e ~20° in alto nella mappa) finisca proprio nel riflesso della vista frontale.
 * Angoli trovati provando dal vivo (window.__eidosVista.luce): -22° attorno all'asse orizzontale, 128° attorno a quello
 * verticale (assi del visore, y in alto).
 * CHORA (13 ott sera, Davide: «troppi riflessi centrali sui modelli Chora: si devono vedere i dettagli»): la piastrina
 * TRAFORATA con il softbox in pieno diventa bianca e la mappa sparisce. Per Chora l'angolo cambia con la finitura
 * (provati dal vivo): argento 180° (grigio medio, la mappa si legge bene), oro giallo e oro rosa 120° (oro pieno, non
 * bruciato, non marrone). Si aggiorna nell'istante in cui il cliente cambia la finitura, senza calcolo.
 */
const LUCE_TYPOS_GRADI = {x: -22, y: 128};
const LUCE_CHORA_GRADI: Record<string, {x: number; y: number}> = {
	argento: {x: 0, y: 180},
	"oro giallo": {x: 0, y: 120},
	"oro rosa": {x: 0, y: 120},
};
function quaternioneLuce(gx: number, gy: number): number[] {
	const ax = (gx * Math.PI) / 180, ay = (gy * Math.PI) / 180;
	const cx = Math.cos(ax / 2), sx = Math.sin(ax / 2), cy = Math.cos(ay / 2), sy = Math.sin(ay / 2);
	return [cy * sx, sy * cx, -sy * sx, cy * cx]; // prima la rotazione orizzontale, poi quella verticale
}
function useLuce(namespaces: string[]) {
	const viewports = useShapeDiverStoreViewport((s) => s.viewports);
	useEffect(() => {
		const {getParameter} = useShapeDiverStoreParameters.getState();
		const typos = namespaces.some((ns) => getParameter(ns, NOME_FOTO));
		const finitura = namespaces.map((ns) => getParameter(ns, NOME_FINITURA)).find(Boolean);
		const applica = () => {
			let g = LUCE_TYPOS_GRADI;
			if (!typos) g = LUCE_CHORA_GRADI[chiaveFinitura(nomeScelta(finitura))] ?? LUCE_CHORA_GRADI.argento;
			const q = quaternioneLuce(g.x, g.y);
			for (const vp of Object.values(viewports)) {
				const v = vp as unknown as {environmentMapRotation: number[]; update: () => void};
				v.environmentMapRotation = q;
				v.update();
			}
		};
		applica();
		if (typos || !finitura) return;
		let prima = ui(finitura);
		return finitura.subscribe(() => {
			const ora = ui(finitura);
			if (ora === prima) return;
			prima = ora;
			applica();
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [namespaces.join("|"), viewports]);
}

export default function EidosVista() {
	const parameterStores = useShapeDiverStoreParameters((s) => s.parameterStores);
	const namespaces = Object.keys(parameterStores);
	// (9 ott 2026) metallo e smalto con il colore nitido anche dopo il calcolo (vedi finituraIstantanea.ts)
	useColoriNitidi();
	// (13 ott 2026) luce: Týpos come quando si gira il gioiello; Chora secondo la finitura (dettagli leggibili)
	useLuce(namespaces);

	useEffect(() => {
		const {getParameter} = useShapeDiverStoreParameters.getState();
		// per le prove dal browser: window.__eidosVista.leggi("Mostra il retro")
		(window as unknown as {__eidosVista: unknown}).__eidosVista = {
			leggi: (nome: string) => {
				for (const ns of namespaces) {
					const st = getParameter(ns, nome);
					if (st) {
						const p = st.getState() as IShapeDiverParameter<string>;
						return {ui: p.state.uiValue, exec: p.state.execValue, dirty: p.state.dirty, iniziale: p.definition.defval, scelte: (p.definition as {choices?: string[]}).choices};
					}
				}
				return null;
			},
			// prova: chiede a ShapeDiver un'esportazione (es. "EXP_TaglioLaser_DXF") con i parametri attuali e
			// restituisce gli indirizzi dei file (costa un'esportazione di crediti)
			esporta: async (nome: string) => {
				const {exportStores} = useShapeDiverStoreParameters.getState();
				for (const ns of Object.keys(exportStores)) {
					for (const st of Object.values(exportStores[ns])) {
						const e = st.getState() as {definition: {name?: string; displayname?: string}; actions: {request: () => Promise<{content?: {href: string; format?: string; size?: number}[]; filename?: string}>}};
						if (e.definition.name === nome || e.definition.displayname === nome) {
							const r = await e.actions.request();
							return {filename: r.filename, content: r.content};
						}
					}
				}
				return null;
			},
			// (5 ott sera) prova: elenco delle USCITE del modello con gli indirizzi dei file di geometria (per misurarne il peso)
			uscite: () => {
				const {sessions} = useShapeDiverStoreSession.getState();
				const out: {sessione: string; nome: string; file: string[]}[] = [];
				for (const [ns, s] of Object.entries(sessions)) {
					const outputs = (s as unknown as {outputs?: Record<string, {name?: string; content?: {href?: string; format?: string; size?: number}[]}>}).outputs ?? {};
					for (const o of Object.values(outputs)) out.push({sessione: ns, nome: o.name ?? "?", file: (o.content ?? []).map((c) => `${c.format ?? ""} ${c.size ?? "?"} ${c.href ?? ""}`)});
				}
				return out;
			},
			// (13 ott) prova: ricolora subito il metallo nel visore (es. "#D99A80"), senza calcolo
			colore: (hex: string) => ricoloraMetallo(hex),
			// (13 ott) prova: luce della scena. Senza argomenti legge; con {rot:[x,y,z,w], intensita, sfocatura} imposta
			// la rotazione della mappa d'ambiente (quaternione), l'intensita' e la sfocatura su tutti i visori
			luce: (o?: {rot?: number[]; intensita?: number; sfocatura?: number; esposizione?: number}) => {
				const out: unknown[] = [];
				for (const vp of Object.values(useShapeDiverStoreViewport.getState().viewports)) {
					const v = vp as unknown as {environmentMapRotation: number[]; environmentMapIntensity: number; environmentMapBlurriness: number; toneMappingExposure?: number; environmentMap: unknown; update: () => void; getEnvironmentMapImageUrl?: () => string};
					if (o?.rot) v.environmentMapRotation = o.rot as unknown as number[];
					if (o?.intensita !== undefined) v.environmentMapIntensity = o.intensita;
					if (o?.sfocatura !== undefined) v.environmentMapBlurriness = o.sfocatura;
					if (o?.esposizione !== undefined) v.toneMappingExposure = o.esposizione;
					if (o) v.update();
					out.push({rot: Array.from(v.environmentMapRotation ?? []), intensita: v.environmentMapIntensity, sfocatura: v.environmentMapBlurriness, esposizione: v.toneMappingExposure, mappa: v.environmentMap, url: v.getEnvironmentMapImageUrl?.()});
				}
				return out;
			},
			// (13 ott) prova: il contenuto dell'uscita dati "Scheda" come lo vede il pannello foto (sagoma.base, foto_px)
			scheda: () => {
				const {sessions} = useShapeDiverStoreSession.getState();
				const out: unknown[] = [];
				for (const s of Object.values(sessions)) {
					const outputs = (s as unknown as {outputs?: Record<string, {name?: string; version?: string; content?: {data?: unknown}[]}>}).outputs ?? {};
					for (const o of Object.values(outputs)) {
						if (o.name !== "Scheda") continue;
						const d = o.content?.[0]?.data;
						const j = (typeof d === "string" ? JSON.parse(d) : d) as {sagoma?: unknown} | null;
						out.push({versione: o.version, sagoma: j?.sagoma});
					}
				}
				return out;
			},
			imposta: (nome: string, v: string) => {
				for (const ns of namespaces) {
					const st = getParameter(ns, nome);
					if (st) {
						const p = st.getState() as IShapeDiverParameter<string>;
						const ok = p.actions.setUiValue(v);
						if (ok) p.actions.execute(true);
						return ok;
					}
				}
				return null;
			},
		};
		const disiscrizioni: (() => void)[] = [];
		for (const ns of namespaces) {
			const incisione = getParameter(ns, NOME_INCISIONE);
			const retro = getParameter(ns, NOME_RETRO);
			const dedica = getParameter(ns, NOME_DEDICA);
			// FORMA <-> FORMA ANELLO: si guarda il valore APPENA SCELTO del Modello (uiValue), prima del calcolo
			const modello = getParameter(ns, NOME_MODELLO);
			const forma = getParameter(ns, NOME_FORMA);
			const formaAnello = getParameter(ns, NOME_FORMA_ANELLO);
			if (modello && forma && formaAnello) {
				let primaUi = String((modello.getState() as IShapeDiverParameter<string>).state.uiValue ?? "");
				disiscrizioni.push(
					modello.subscribe(() => {
						const oraUi = String((modello.getState() as IShapeDiverParameter<string>).state.uiValue ?? "");
						if (oraUi === primaUi) return;
						const eraAnello = nomeScelta(modello, primaUi) === NOME_ANELLO;
						const oraAnello = nomeScelta(modello, oraUi) === NOME_ANELLO;
						primaUi = oraUi;
						if (oraAnello && !eraAnello) committa(formaAnello, indiceScelta(formaAnello, nomeScelta(forma)));
						else if (eraAnello && !oraAnello) committa(forma, indiceScelta(forma, nomeScelta(formaAnello)));
					}),
				);
			}
			// (16 ott 2026) ANELLO come nelle foto dei pulsanti: scegliendo il modello o una forma la camera torna alla vista
			// "di base" (anello: quella delle foto; uscendo dall'anello: di fronte), vedi sceltaGioiello in zoomDedica.ts
			if (modello) {
				const anello = () => nomeScelta(modello) === NOME_ANELLO;
				sceltaGioiello(anello());
				// (16 ott 2026, Davide) Týpos: nell'anello il testo sta su UNA riga e il menu "Allineamento" sparisce (filtro nel
				// modello): scegliendo l'anello l'allineamento torna "Centrato" (con "Giustificato" la riga unica andava a sinistra)
				const allinea = getParameter(ns, "Allineamento");
				if (allinea) {
					let primaM = ui(modello);
					disiscrizioni.push(
						modello.subscribe(() => {
							const ora = ui(modello);
							if (ora === primaM) return;
							primaM = ora;
							if (anello() && ui(allinea) !== "0") committaValore(allinea, "0");
						}),
					);
				}
				for (const st of [modello, forma, formaAnello]) {
					if (!st) continue;
					let primaV = ui(st);
					disiscrizioni.push(
						st.subscribe(() => {
							const ora = ui(st);
							if (ora === primaV) return;
							primaV = ora;
							sceltaGioiello(anello());
						}),
					);
				}
			}
			// FINITURA ISTANTANEA: nell'istante della scelta (uiValue, prima del calcolo) il metallo prende il colore
			// nuovo nel visore; il calcolo di ShapeDiver parte come sempre e porta poi la geometria gia' colorata
			const finitura = getParameter(ns, NOME_FINITURA);
			if (finitura) {
				let primaF = ui(finitura);
				disiscrizioni.push(
					finitura.subscribe(() => {
						const oraF = ui(finitura);
						if (oraF === primaF) return;
						const vecchia = nomeScelta(finitura, primaF);
						primaF = oraF;
						finituraCambiata(nomeScelta(finitura, oraF), vecchia);
					}),
				);
			}
			// SMALTO ISTANTANEO: da un colore a un altro il pallino/casetta/cuore si ricolora subito; acceso/spento resta un calcolo
			const smalto = getParameter(ns, NOME_SMALTO);
			if (smalto) {
				let primaS = ui(smalto);
				disiscrizioni.push(
					smalto.subscribe(() => {
						const oraS = ui(smalto);
						if (oraS === primaS) return;
						const vecchiaS = nomeScelta(smalto, primaS);
						primaS = oraS;
						smaltoCambiato(nomeScelta(smalto, oraS), vecchiaS);
					}),
				);
			}
			if (!retro) continue;
			// dedica scritta -> retro
			if (incisione) {
				let prima = ui(incisione);
				disiscrizioni.push(
					incisione.subscribe(() => {
						const ora = ui(incisione);
						if (ora === prima) return;
						prima = ora;
						// "Dedica" = 1 (Voglio una dedica) e testo non vuoto -> si gira sul retro (nello stesso calcolo
						// della dedica) e si inquadra da vicino la piastrina con la scritta; testo cancellato -> vista normale
						if (ora.trim() && (!dedica || ui(dedica) !== "0")) {
							committaValore(retro, "1");
							// (16 ott 2026) solo la prima volta si gira: poi il gioiello resta dove l'ha messo il cliente
							zoomDedicaTesto();
						} else if (!ora.trim()) {
							zoomDedicaOff(fattoreNormale());
						}
					}),
				);
			}
			// (15 ott 2026, Týpos) cambiando carattere o allineamento del testo del retro il gioiello si gira sul retro
			for (const nome of ["Carattere", "Allineamento"]) {
				const st = getParameter(ns, nome);
				if (!st || !incisione) continue;
				let primaC = ui(st);
				disiscrizioni.push(
					st.subscribe(() => {
						const ora = ui(st);
						if (ora === primaC) return;
						primaC = ora;
						if (ui(incisione).trim() && (!dedica || ui(dedica) !== "0")) {
							committaValore(retro, "1");
							zoomDedicaTesto();
						}
					}),
				);
			}
			// (5 ott) "Dedica" spenta -> fronte e vista normale; riaccesa con un testo gia' scritto -> retro + zoom
			if (dedica) {
				let primaD = ui(dedica);
				disiscrizioni.push(
					dedica.subscribe(() => {
						const ora = ui(dedica);
						if (ora === primaD) return;
						primaD = ora;
						if (ora === "0") {
							committaValore(retro, "0");
							zoomDedicaOff(fattoreNormale());
						} else if (incisione && ui(incisione).trim()) {
							committaValore(retro, "1");
							zoomDedicaOn();
						}
					}),
				);
			}
			// (16 ott 2026, Týpos, Davide: «se ho scelto di personalizzare il retro e cambio la forma, la personalizzazione
			// deve rimanere attiva») nel Týpos (c'e' il parametro "Foto") il pulsante "Mostra il retro" non c'e' piu': con il
			// testo del retro attivo, cambiando forma/modello/misura/finitura/cornice il gioiello RESTA sul retro; torna sul
			// fronte quando il cliente carica una foto nuova.
			const typos = !!getParameter(ns, NOME_FOTO);
			const retroAttivo = () => !!(typos && incisione && ui(incisione).trim() && (!dedica || ui(dedica) !== "0"));
			// altre scelte -> fronte (Týpos con il testo del retro: resta sul retro)
			for (const nome of RIMETTONO_DRITTO) {
				const st = getParameter(ns, nome);
				if (!st) continue;
				let prima = ui(st);
				disiscrizioni.push(
					st.subscribe(() => {
						const ora = ui(st);
						if (ora === prima) return;
						prima = ora;
						if (retroAttivo()) {
							committaValore(retro, "1");
							zoomDedicaOn();
							return;
						}
						committaValore(retro, "0"); // nello stesso calcolo della scelta: niente secondo calcolo
						zoomDedicaOff(fattoreNormale());
					}),
				);
			}
			// Týpos: una foto nuova si guarda sul fronte (non zoom/sposta: li riazzera anche il cambio di forma, che deve
			// lasciare il gioiello sul retro)
			if (typos) {
				for (const nome of [NOME_FOTO]) {
					const st = getParameter(ns, nome);
					if (!st) continue;
					let primaF = ui(st);
					disiscrizioni.push(
						st.subscribe(() => {
							const ora = ui(st);
							if (ora === primaF) return;
							primaF = ora;
							if (ui(retro) === "1") {
								committaValore(retro, "0");
								zoomDedicaOff(fattoreNormale());
							}
						}),
					);
				}
			}
		}
		// GIOIELLO SAGOMA (12 ott sera): cambio di forma della maschera -> zoom 100 e spostamento 0, nello stesso calcolo
		for (const ns of namespaces) {
			const zoomS = getParameter(ns, NOME_ZOOM_SAGOMA);
			const sxS = getParameter(ns, NOME_SPOSTA_X);
			const syS = getParameter(ns, NOME_SPOSTA_Y);
			if (!zoomS || !sxS || !syS) continue;
			for (const nome of CAMBIANO_MASCHERA) {
				const st = getParameter(ns, nome);
				if (!st) continue;
				let prima = ui(st);
				disiscrizioni.push(
					st.subscribe(() => {
						const ora = ui(st);
						if (ora === prima) return;
						prima = ora;
						committaValore(zoomS, "100");
						committaValore(sxS, "0");
						committaValore(syS, "0");
					}),
				);
			}
			// (13 ott) anche una FOTO NUOVA torna alla misura massima: il valore del parametro e' un File appena scelto
			// (poi diventa l'id del caricamento, che non conta); foto tolta = torna l'esempio, anche li' si rimassimizza
			const foto = getParameter(ns, NOME_FOTO);
			if (foto) {
				const chiaveFoto = () => {
					const v = (foto.getState() as IShapeDiverParameter<unknown>).state.uiValue;
					if (v instanceof File) return `file|${v.name}|${v.size}|${v.lastModified}`;
					return v ? "" : "vuota";
				};
				let primaF = chiaveFoto();
				disiscrizioni.push(
					foto.subscribe(() => {
						const ora = chiaveFoto();
						if (!ora || ora === primaF) return;
						primaF = ora;
						committaValore(zoomS, "100");
						committaValore(sxS, "0");
						committaValore(syS, "0");
					}),
				);
			}
		}
		return () => disiscrizioni.forEach((d) => d());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [namespaces.join("|")]);

	return null;
}
